import React from "react";
import { useSelector } from "react-redux";
import { Text } from "components";
import { selectGameObject } from "features/gamestate/gameStateSlice";
import { selectAllPlayers } from "features/player/playerSlice";
import { selectLeaderboardPlayers } from "features/leaderboard/leaderboardSlice";
import {
  getDisplayStatus,
  computeCompletion,
  getRoundCount,
  PUBLISH_STATUS,
  DISPLAY_STATUS_AWAITING,
} from "../lifecycle";

/**
 * The band above the Game Home hero (spec feedback — mirrors the dashboard's
 * greeting + stat tiles). A state-aware orientation line plus three at-a-glance
 * tiles (clues / rounds / players) for this game.
 */
const greetingFor = (status, { writtenLeft, roundCount, winnerName }) => {
  switch (status) {
    case DISPLAY_STATUS_AWAITING:
      return { title: "All clues written 🎉", sub: "Give it a final pass, then mark it ready to launch." };
    case PUBLISH_STATUS.READY:
      return { title: "Ready to launch 🚀", sub: "Your board's all set — launch whenever you are." };
    case PUBLISH_STATUS.IN_PROGRESS:
      return { title: "Game in progress", sub: "You're live — open the Control Room to host." };
    case PUBLISH_STATUS.PLAYED:
      return {
        title: "That's a wrap 🎬",
        sub: winnerName
          ? `${winnerName} took it — copy & replay for a rematch.`
          : "Nicely done. Copy & replay for a rematch.",
      };
    case PUBLISH_STATUS.ARCHIVED:
      return { title: "Archived", sub: "Restore this game from Settings to play it again." };
    default: // draft < 100%
      return {
        title: "Let's finish your game",
        sub:
          writtenLeft > 0
            ? `${writtenLeft} ${writtenLeft === 1 ? "clue" : "clues"} left to write across ${roundCount} ${roundCount === 1 ? "round" : "rounds"}.`
            : "Keep building — you're almost there.",
      };
  }
};

const GameHomeHeader = () => {
  const game = useSelector(selectGameObject);
  const players = useSelector(selectAllPlayers);
  const leaderboard = useSelector(selectLeaderboardPlayers);

  if (!game) return null;

  const status = getDisplayStatus(game);
  const { written, total } = computeCompletion(game);
  const roundCount = getRoundCount(game);
  const winner = leaderboard?.[0];
  const greeting = greetingFor(status, {
    writtenLeft: Math.max(0, total - written),
    roundCount,
    winnerName: winner?.displayName || winner?.name,
  });

  const tiles = [
    { value: `${written}/${total}`, label: "clues" },
    { value: roundCount, label: roundCount === 1 ? "round" : "rounds" },
    { value: players.length, label: players.length === 1 ? "player" : "players" },
  ];

  return (
    <div className="gx-gh-greet">
      <div className="gx-gh-greet-text">
        <Text variant="h2" className="gx-gh-greet-title">{greeting.title}</Text>
        <Text variant="body" tone="muted" className="gx-gh-greet-sub">{greeting.sub}</Text>
      </div>
      <div className="gx-gh-greet-stats">
        {tiles.map((t) => (
          <div key={t.label} className="gx-gh-stat-tile">
            <div className="gx-gh-stat-num">{t.value}</div>
            <div className="gx-gh-stat-label">{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GameHomeHeader;
